import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const items = [{
  emoji: '🎯',
  title: 'The 6 Core Volumes',
  desc: 'A complete pathway from U6s to Seniors + Methodology Volume. Covering core skills, rucking, set-piece, and advanced strike moves.',
  stat: '6 PDFs · 800+ pages',
  color: 'bg-blue-500',
  shadow: 'shadow-[0_0_15px_rgba(59,130,246,0.5)]',
  chipColor: 'text-blue-300 bg-blue-500/15 border-blue-500/30'
}, {
  emoji: '📋',
  title: 'The Drill Library',
  desc: 'Crystal-clear diagrams and coaching points for every situation — warm-ups, breakdown, tackle tech, set-plays.',
  stat: 'Updated 2026',
  color: 'bg-green-500',
  shadow: 'shadow-[0_0_15px_rgba(34,197,94,0.5)]',
  chipColor: 'text-cta-green bg-cta-green/15 border-cta-green/30'
}, {
  emoji: '🗓️',
  title: 'The Planning Suite',
  desc: 'Season templates for pre-season, mid-season, and finals — ready to drop into your club calendar.',
  stat: 'Season-ready',
  color: 'bg-purple-500',
  shadow: 'shadow-[0_0_15px_rgba(168,85,247,0.5)]',
  chipColor: 'text-purple-300 bg-purple-500/15 border-purple-500/30'
}, {
  emoji: '💪',
  title: 'Physical Prep & S&C',
  desc: 'Age-appropriate strength and conditioning to keep players safe, powerful, and injury-free all season.',
  stat: '+50 S&C drills',
  color: 'bg-orange-500',
  shadow: 'shadow-[0_0_15px_rgba(249,115,22,0.5)]',
  chipColor: 'text-orange-300 bg-orange-500/15 border-orange-500/30'
}];
const contentStats = [{
  value: '6',
  label: 'PDF Volumes'
}, {
  value: '+150',
  label: 'Session plans ready'
}, {
  value: '+100',
  label: 'Drills with diagrams'
}, {
  value: 'All',
  label: 'Age grades U6s–Seniors'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/bWQC3kcFzT9r28iEvUhPqk/Captura-de-pantalla-2026-05-29-a-las-12.07.04_a._m..webp',
  alt: 'Chapter 3 — Contact Preparation: The Safe-Rugby Protocol'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/vXvEHfuBN4itJduqPyXaWY/Captura-de-pantalla-2026-05-29-a-las-12.07.40_a._m..webp',
  alt: 'Ruck Defence — Visual Setup (Pillar, Post, Key)'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/gvESzRu2ob9sSqn67aFBGw/Captura-de-pantalla-2026-05-29-a-las-12.11.11_a._m..webp',
  alt: 'The 1st Clearer: Dominate the Space'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/eTFGuTnN15YQgWfJESD7az/Captura-de-pantalla-2026-05-29-a-las-12.11.31_a._m..webp',
  alt: 'Linear Speed Mechanics — Piston Drive & 0-10m Blast'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pwkiShG72xg2L4bzMu4oLG/Captura-de-pantalla-2026-05-29-a-las-12.11.57_a._m..webp',
  alt: 'Breakdown Drills 41–60: Master Reference Table'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      const center = scrollLeft + clientWidth / 2;
      const children = container.children;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-20 px-4" data-id="element-60">
      <div className="max-w-5xl mx-auto" data-id="element-61">
        <div className="text-center mb-4" data-id="element-62">
          <span className="inline-flex items-center gap-2 text-primary font-bold text-xs sm:text-sm uppercase tracking-wider bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full" data-id="element-63">
            📦 What's inside the Bundle
          </span>
        </div>

        <div className="text-center mb-10" data-id="element-64">
          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-65">
            Everything you need.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400" data-id="element-66">
              Nothing you don't.
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-xl max-w-2xl mx-auto" data-id="element-67">
            Digital PDFs ready to print or open on your tablet —{' '}
            <span className="text-text-main font-bold" data-id="element-68">
              downloadable in 30 seconds
            </span>{' '}
            after checkout.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-14 bg-bg-alt border border-border-light rounded-2xl p-5 sm:p-6" data-id="element-69">
          {contentStats.map((s, i) => <div key={i} className={`text-center ${i < contentStats.length - 1 ? 'sm:border-r border-border-light' : ''}`} data-id="element-70">
              <div className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-400 font-black text-2xl sm:text-4xl leading-none tracking-tight" data-id="element-71">
                {s.value}
              </div>
              <p className="text-text-sec text-[11px] sm:text-sm font-medium mt-2 leading-tight" data-id="element-72">
                {s.label}
              </p>
            </div>)}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-20" data-id="element-73">
          {items.map((item, i) => <div key={i} className="bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 hover:border-white/20 transition-all duration-300" data-id="element-74">
              <div className={`${item.color} h-1.5 w-full ${item.shadow}`} data-id="element-75" />
              <div className="flex-1 flex flex-col p-7 sm:p-8 relative" data-id="element-76">
                <div className={`absolute inset-0 ${item.color} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity`} data-id="element-77"></div>

                <div className="flex items-center justify-between gap-3 mb-5 relative z-10" data-id="element-78">
                  <span className="text-5xl block group-hover:scale-110 transition-transform duration-300" data-id="element-79">
                    {item.emoji}
                  </span>
                  <span className={`text-[10px] sm:text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full border ${item.chipColor} whitespace-nowrap`} data-id="element-80">
                    {item.stat}
                  </span>
                </div>

                <h3 className="text-xl sm:text-2xl font-black text-text-main leading-snug relative z-10 mb-3" data-id="element-81">
                  {item.title}
                </h3>
                <p className="text-text-sec text-base leading-relaxed relative z-10" data-id="element-82">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>

        <div className="relative" data-id="element-83">
          <div className="text-center mb-10" data-id="element-84">
            <span className="inline-flex items-center gap-2 text-secondary font-bold text-xs sm:text-sm uppercase tracking-wider bg-secondary/10 border border-secondary/20 px-4 py-1.5 rounded-full mb-4" data-id="element-85">
              📸 Real preview
            </span>
            <h3 className="text-2xl sm:text-3xl font-black text-text-main mt-3" data-id="element-86">
              Look inside:{' '}
              <span className="text-text-sec font-bold" data-id="element-87">
                sessions ready to print and run today
              </span>
            </h3>
            <p className="text-text-sec text-sm mt-3 flex items-center justify-center gap-2" data-id="element-88">
              <span className="inline-flex items-center gap-1.5 sm:hidden" data-id="element-89">
                <ChevronLeft className="w-4 h-4 animate-pulse-composited" data-id="element-90" />
                Swipe to see more
                <ChevronRight className="w-4 h-4 animate-pulse-composited" data-id="element-91" />
              </span>
              <span className="text-text-sec text-xs font-medium" data-id="element-92">
                {activeIndex + 1} / {carouselImages.length}
              </span>
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-93">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main transition-opacity ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Previous" data-id="element-94">
              <ChevronLeft className="w-6 h-6" data-id="element-95" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main transition-opacity ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Next" data-id="element-96">
              <ChevronRight className="w-6 h-6" data-id="element-97" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-98">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.3)] transition-transform duration-300 ${activeIndex === i ? 'scale-100 opacity-100' : 'scale-95 opacity-50'}`} data-id="element-99">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white/5" data-id="element-100" />
                </div>)}
            </div>
          </div>

          <div className="flex justify-center gap-0 mt-2" data-id="element-101">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="flex items-center justify-center min-w-[48px] min-h-[48px] p-3" aria-label={`Go to slide ${i + 1}`} data-id="element-102">
                <span className={`block h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-2'}`} data-id="element-103" />
              </button>)}
          </div>
        </div>

        <div className="mt-16 text-center bg-gradient-to-br from-primary/10 to-cta-green/5 border border-cta-green/30 rounded-2xl p-6 sm:p-8 relative overflow-hidden" data-id="element-104">
          <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(circle at center, rgba(22,199,132,0.08) 0%, transparent 70%)'
        }} data-id="element-105"></div>
          <p className="relative text-text-main text-lg sm:text-xl font-bold leading-snug mb-2" data-id="element-106">
            All of this is{' '}
            <span className="text-cta-green" data-id="element-107">included in the Kit</span> —
            downloadable today.
          </p>
          <p className="relative text-text-sec text-sm sm:text-base mb-5" data-id="element-108">
            Real value $300 ·{' '}
            <span className="text-text-main font-bold" data-id="element-109">Just $27 USD</span> in
            launch offer.
          </p>
          <a href="#pricing" className="relative inline-flex items-center gap-2 text-cta-green font-black text-base sm:text-lg hover:gap-3 transition-all border-b-2 border-cta-green/40 hover:border-cta-green pb-1" data-id="element-110">
            👉 I want the complete Kit for $27 USD →
          </a>
        </div>
      </div>
    </section>;
}